/**
 * Cross-cutting "is a call active" pubsub so background audio (Radio) can
 * react to call state without coupling to any specific call provider.
 *
 * Contract:
 *  - Producers call `setCallActive(true)` when an incoming call is ringing,
 *    or when an outbound call enters connecting/connected.
 *  - Producers call `setCallActive(false)` once the call ends or is rejected.
 *  - Subscribers (e.g. RadioProvider) receive the new boolean and act on it.
 *    A transition to `true` MUST be treated as a hard user-intent stop so
 *    media does NOT auto-resume after the call ends.
 */

type Listener = (active: boolean) => void;

const listeners = new Set<Listener>();
let current = false;

export function isCallActive(): boolean {
  return current;
}

export function setCallActive(active: boolean): void {
  if (active === current) return;
  current = active;
  for (const cb of listeners) {
    try { cb(active); } catch { /* listener errors are swallowed */ }
  }
}

export function subscribeCallActive(cb: Listener): () => void {
  listeners.add(cb);
  // Emit current state immediately so late subscribers stay in sync.
  try { cb(current); } catch { /* noop */ }
  return () => { listeners.delete(cb); };
}
